import { useState, CSSProperties } from 'react';
import { Icon, Image, Label } from 'semantic-ui-react';

import { formatDuration, isVideoContentType } from '../utils/media';

interface MediaTileProps {
    /** Presigned S3 URL, or a local object URL for a not-yet-uploaded file. */
    url: string;
    /** From `Image.contentType`, or `File.type` when previewing a staged file. Null/absent means photo. */
    contentType?: string | null;
    durationSeconds?: number | null;
    alt?: string;
    /** Native video controls. Off for small thumbnails, on wherever the clip is meant to be watched. */
    controls?: boolean;
    fluid?: boolean;
    rounded?: boolean;
    bordered?: boolean;
    style?: CSSProperties;
    className?: string;
}

/** Semantic UI's `rounded` border radius, so video tiles match the photo tiles beside them. */
const ROUNDED_RADIUS = '0.3125rem';

/**
 * Renders one piece of post media — photo or video — so every gallery, composer and review
 * screen treats the two the same way.
 *
 * Videos are stored untranscoded, so some browsers can't decode an iPhone HEVC .mov. We let
 * the browser try and fall back on the real `error` event rather than pre-screening with
 * canPlayType — that reports '' for 'video/quicktime' in Chrome even though Chrome plays most
 * H.264 .mov files, so pre-screening would hide clips that actually work.
 */
export const MediaTile = ({
    url,
    contentType,
    durationSeconds,
    alt = '',
    controls = false,
    fluid,
    rounded,
    bordered,
    style,
    className,
}: MediaTileProps) => {
    const [failed, setFailed] = useState(false);

    if (!isVideoContentType(contentType)) {
        return (
            <Image
                src={url}
                alt={alt}
                fluid={fluid}
                rounded={rounded}
                bordered={bordered}
                style={style}
                className={className}
            />
        );
    }

    const radius = rounded ? ROUNDED_RADIUS : undefined;
    const wrapperStyle: CSSProperties = {
        position: 'relative',
        display: fluid ? 'block' : 'inline-block',
        width: fluid ? '100%' : undefined,
        lineHeight: 0,
    };

    if (failed) {
        return (
            <div className={className} style={wrapperStyle}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5em',
                        padding: '1em',
                        minHeight: '100px',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        lineHeight: 1.3,
                        background: '#f3f4f5',
                        color: '#767676',
                        fontSize: '0.85em',
                        borderRadius: radius,
                        ...style,
                    }}
                >
                    <Icon name="file video outline" size="large" style={{ margin: 0 }} />
                    <span>This video can&apos;t be played in this browser — open it in Safari or the Joyya app.</span>
                </div>
            </div>
        );
    }

    return (
        <div className={className} style={wrapperStyle}>
            <video
                src={url}
                muted
                playsInline
                preload="metadata"
                controls={controls}
                onError={() => setFailed(true)}
                style={{
                    display: 'block',
                    width: fluid ? '100%' : undefined,
                    borderRadius: radius,
                    border: bordered ? '1px solid rgba(0,0,0,.1)' : undefined,
                    background: '#000',
                    ...style,
                }}
            />
            {/* Without controls there is nothing to signal this is a clip, not a still. */}
            {!controls && (
                <Label
                    size="mini"
                    style={{
                        position: 'absolute',
                        bottom: '6px',
                        left: '6px',
                        background: 'rgba(0,0,0,.65)',
                        color: '#fff',
                        padding: '.3em .5em',
                        lineHeight: 1,
                        pointerEvents: 'none',
                    }}
                >
                    <Icon name="play" style={{ margin: durationSeconds ? '0 .3em 0 0' : 0 }} />
                    {durationSeconds ? formatDuration(durationSeconds) : null}
                </Label>
            )}
        </div>
    );
};
